import {useQueryClient} from '@tanstack/react-query'
import {Req} from 'quickstart/types'
import * as R from 'rambdax'
import {Fragment} from 'react'
import * as Final from 'react-final-form'
import {
  MetaObject,
  Offer,
  api,
  logger,
  meta,
  semiSplit,
  stableJson,
} from 'tizra'
import * as B from '../block'
import {Config} from './admin'
import {flexAlign} from './helpers'

const log = logger('quickstart/blocks/HeaderBlock')

/**
 * Check if any of the offers (return value from relevant-offers API) is already
 * in the cart, by combination of offer id and item id.
 *
 * @return string tizraId, null for none, or undefined for unknown yet.
 */
const useAlreadyInCart = (offers: Array<Offer>): string | null | undefined => {
  const {data: cart} = B.useApi.cart()
  if (cart) {
    for (const c of cart) {
      for (const o of offers) {
        if (
          c.offer.tizraId === o.tizraId &&
          c.item.tizraId === o.controlled.tizraId
        ) {
          return o.tizraId
        }
      }
    }
    return null
  }
}

const prefixMatch = (offer: Offer) => {
  const m = meta.string(offer, 'offerName')?.match(/^([^:]+)(:)\s+(\S.*)$/s)
  return m && {lhs: m[1].trim(), sep: m[2], rhs: m[3].trim()}
}

const OfferPrice = ({
  offer,
  labelSeparator = ':',
  offerName = meta.string(offer, 'offerName'),
}: {
  offer: Offer
  labelSeparator?: string
  offerName?: string
}) => (
  <Fragment>
    {offerName}
    {!!offerName && labelSeparator + ' '}
    <B.Currency amount={offer.price} currency={offer.currencyInfo || 'USD'} />
  </Fragment>
)

const OfferBlurb = ({
  offer,
  alignment,
}: {
  offer: Offer
  alignment: Config['alignment']
}) => (
  <B.MetaValue
    metaObj={offer}
    prop="buyingBlurb"
    as={B.Text}
    textAlign={alignment}
    html={true /* if html, don't simplify */}
  />
)

const MultipleOfferLabel = ({
  offers,
  includeBlurb = true,
}: Pick<Config, 'alignment'> & {offers: Offer[]; includeBlurb?: boolean}) => {
  const prefixes = offers.map(prefixMatch)
  const commonPrefix =
    prefixes.length > 1 &&
    prefixes.every(p => p && p.lhs === prefixes[0]!.lhs) &&
    prefixes[0]!.lhs

  return (
    <B.Stack spacing="xxs">
      {commonPrefix && <B.Text>{commonPrefix}</B.Text>}
      <B.Text>
        {offers.map((offer, i) => {
          const offerName = commonPrefix
            ? prefixes[i]!.rhs
            : meta.string(offer, 'offerName')
          const price = <OfferPrice offer={offer} offerName={offerName} />
          return (
            <Fragment key={i}>
              {i === 0 ? (
                <strong>{price}</strong>
              ) : (
                <span>
                  <br />
                  {price}
                </span>
              )}
            </Fragment>
          )
        })}
      </B.Text>
      {includeBlurb && <OfferBlurb offer={offers[0]} alignment="left" />}
    </B.Stack>
  )
}

/**
 * MultipleOfferGroups means two or more groups of offers with different term
 * sheets. Present choices with radio buttons. If there are multiple offers within
 * a radio button, then automatically choose the least expensive, since they have
 * the same term sheets.
 */
const MultipleOfferGroups = ({
  alignment,
  groupedOffers,
  metaObj,
  tizraId = metaObj?.tizraId,
}: Pick<Config, 'alignment'> &
  B.MetaProps<MetaObject> & {groupedOffers: Offer[][]}) => {
  const alreadyInCart = useAlreadyInCart(groupedOffers.flat())
  const queryClient = useQueryClient()

  const blurbs = groupedOffers
    .flat()
    .map(offer => meta.string(offer, 'buyingBlurb')?.trim())

  const haveBlurbs = blurbs.some(Boolean)

  // If blurbs are equal, we can show just one, below the radio buttons. If
  // blurbs aren't equal, then we need to show them associated with the
  // individual choices.
  const blurbsAreEqual = blurbs.every(b => b === blurbs[0])

  // Final Form will call onSubmit with the object of values, which consists
  // solely of offerId.
  const onSubmit = async ({offerId}: any) => {
    // Sanity checks, even though these messages won't be rendered.
    if (!offerId) {
      log.warn('Attempted to add undefined offerId to cart')
      return {offerId: 'Missing value'}
    }
    if (!tizraId) {
      log.warn('Attempted to add undefined tizraId to cart')
      return {tizraid: 'Missing value'}
    }

    await api.cartAdd({offerId, objectId: tizraId})

    // Add notification bubble to cart icon.
    queryClient.invalidateQueries(['cart'])

    // Return undefined so Final Form knows that the
    // form was submitted successfully.
  }

  // Select the already-added offer, or the first radio option by default.
  const initialValues = {offerId: alreadyInCart || groupedOffers[0][0].tizraId}

  return (
    <Final.Form initialValues={initialValues} onSubmit={onSubmit}>
      {({handleSubmit, submitting, submitSucceeded}) => (
        <form onSubmit={handleSubmit}>
          <B.Stack spacing="xxl">
            <B.Stack spacing="lg">
              <Final.Field name="offerId">
                {({input}) => (
                  <B.Radio.Group
                    options={groupedOffers.map(offers => ({
                      value: offers[0].tizraId,
                      label: (
                        <MultipleOfferLabel
                          alignment={alignment}
                          offers={offers}
                          includeBlurb={haveBlurbs && !blurbsAreEqual}
                        />
                      ),
                    }))}
                    spacing="lg"
                    {...input}
                  />
                )}
              </Final.Field>
              {haveBlurbs && blurbsAreEqual && (
                <OfferBlurb
                  offer={groupedOffers[0][0]}
                  alignment={alignment === 'right' ? 'left' : alignment}
                />
              )}
            </B.Stack>
            <B.Button
              fluid
              type="submit"
              disabled={alreadyInCart !== null || submitting || submitSucceeded}
            >
              {alreadyInCart || submitSucceeded
                ? 'Added to your bag!'
                : 'Add to bag'}
            </B.Button>
          </B.Stack>
        </form>
      )}
    </Final.Form>
  )
}

/**
 * SingleOfferGroup means one or more offers with the same term sheet. Choose
 * and highlight the least expensive applicable offer; the others are shown
 * de-emphasized for comparison.
 */
const SingleOfferGroup = ({
  alignment,
  offers,
  tizraId,
}: Pick<Config, 'alignment'> &
  Req<B.MetaProps, 'tizraId'> & {offers: Offer[]}) => {
  const alreadyInCart = useAlreadyInCart(offers)
  const queryClient = useQueryClient()

  // Caller has already moved the preferred offer to the first position,
  // otherwise maintaining the sort.
  const [bestOffer, ...otherOffers] = offers

  const onSubmit = async () => {
    await api.cartAdd({
      offerId: bestOffer.tizraId,
      objectId: tizraId,
    })

    // Add notification bubble to cart icon.
    queryClient.invalidateQueries(['cart'])

    // Return undefined so Final Form knows that the
    // form was submitted successfully.
  }

  return (
    <Final.Form onSubmit={onSubmit}>
      {({handleSubmit, submitting, submitSucceeded}) => (
        <form onSubmit={handleSubmit}>
          <B.Stack spacing="xxl">
            <B.Stack spacing="lg" wrapChildren={false}>
              <B.Text fontSize="18px" textAlign={alignment}>
                <strong>
                  <OfferPrice offer={bestOffer} />
                </strong>
              </B.Text>
              {otherOffers.map(offer => (
                <B.Text textAlign={alignment} key={offer.tizraId}>
                  <OfferPrice offer={offer} />
                </B.Text>
              ))}
              <OfferBlurb offer={bestOffer} alignment={alignment} />
            </B.Stack>
            <div>
              <B.Button
                fluid
                type="submit"
                disabled={
                  alreadyInCart !== null || submitting || submitSucceeded
                }
              >
                {alreadyInCart || submitSucceeded
                  ? 'Added to your bag!'
                  : 'Add to bag'}
              </B.Button>
            </div>
          </B.Stack>
        </form>
      )}
    </Final.Form>
  )
}

type GainProps = Pick<Config, 'alignment'> &
  B.MetaProps<MetaObject> & {offers?: Offer[]}

const cheapest = (offers: Offer[]) =>
  offers.reduce((best, offer) => (offer.price < best.price ? offer : best))

const cheapestFirst = (offers: Offer[]) => {
  const first = cheapest(offers)
  return first ? [first, ...offers.filter(offer => offer !== first)] : offers
}

export const Gain = ({alignment, offers, tizraId}: GainProps) => {
  if (!offers?.length || !tizraId) {
    return null
  }

  // There are two kinds of offers on collections: distributive and
  // non-distributive. In the former case, the offer applies to each individual
  // item in the collection; when we receive it from the API, it appears to be an
  // offer directly on the item. In the latter case, the offer applies to the
  // entire collection as a whole. Filter the non-distributive offers into
  // a separate list.
  const [itemOffers, collectionOffers] = R.partition(
    offer => offer.controlled.tizraId === tizraId,
    offers,
  )

  // For the collection offers, boil these down to unique tizraId.
  const collectionMetas = R.uniqBy(
    meta => meta.tizraId,
    collectionOffers.map(offer => offer.controlled),
  )

  // For the item offers, group by equivalent term sheets, and make the cheapest
  // the first one.
  const groupedOffers = R.piped(
    itemOffers,
    R.groupBy(offer => stableJson(offer.termSheet)),
    R.values,
    R.map(cheapestFirst),
  )

  log.log({
    offers,
    itemOffers,
    collectionOffers,
    collectionMetas,
    groupedOffers,
  })

  const collectionIntro = `
    This item can ${groupedOffers.length ? 'also' : ''} be purchased as part of
    the following:
  `

  return (
    <B.Box
      display="flex"
      justifyContent={
        alignment === 'left'
          ? 'flex-start'
          : alignment === 'right'
          ? 'flex-end'
          : 'center'
      }
    >
      <B.Stack alignItems={flexAlign(alignment)} spacing="xxl" maxWidth="50%">
        {!!collectionMetas.length && (
          <B.Text textAlign={alignment}>
            {collectionIntro}
            {collectionMetas.map((meta, i) => (
              <Fragment key={meta.tizraId}>
                {i === 0
                  ? ' '
                  : i === collectionMetas.length - 1
                  ? ', and '
                  : ', '}
                <B.MetaLink {...meta} />
              </Fragment>
            ))}
          </B.Text>
        )}
        {!groupedOffers.length ? null : groupedOffers.length === 1 ? (
          <SingleOfferGroup
            alignment={alignment}
            offers={groupedOffers[0]}
            tizraId={tizraId}
          />
        ) : (
          <MultipleOfferGroups
            alignment={alignment}
            groupedOffers={groupedOffers}
            tizraId={tizraId}
          />
        )}
      </B.Stack>
    </B.Box>
  )
}

Gain.displayName = 'HeaderBlock.Gain'

type UseOffersProps = Pick<Config, 'callsToAction'> & B.MetaProps

type UseOffersReturn = undefined | Offer[]

export const useOffers = ({
  callsToAction: {
    gain: {
      requiredTags,
      //sortBy
    },
  },
  tizraId,
}: UseOffersProps): UseOffersReturn => {
  // TODO sort
  return B.useApi.relevantOffers(
    tizraId && {tizraId, requiredTags: semiSplit(requiredTags)},
  ).data as undefined | Offer[]
}
